import { useEffect, useState } from 'react'

const CHECK_EVERY = 5 * 60_000
const LAST_SEEN_KEY = 'tarmx:last-version'

/** What `/changelog.json` holds: the changes of this build, written from the merged pull requests. */
export interface Changelog {
  /** Commit of the build. */
  sha: string
  /** Day of the release, already formatted. */
  date: string
  /** One line per change, for users. */
  changes: string[]
}

async function fetchJson<T>(path: string): Promise<T | null> {
  try {
    const response = await fetch(path, { cache: 'no-store' })
    return response.ok ? ((await response.json()) as T) : null
  } catch {
    return null
  }
}

/**
 * Whether a newer build is deployed: compares `/version.json` with this build,
 * every 5 minutes and when the tab comes back into view.
 * @returns `true` once a new version is online.
 */
export function useUpdateAvailable(): boolean {
  const [available, setAvailable] = useState(false)

  useEffect(() => {
    if (__BUILD__.sha === 'dev') return
    async function check() {
      const deployed = await fetchJson<{ sha: string }>('/version.json')
      if (deployed && deployed.sha !== __BUILD__.sha) setAvailable(true)
    }
    function onVisible() {
      if (document.visibilityState === 'visible') void check()
    }
    const timer = window.setInterval(check, CHECK_EVERY)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return available
}

function readLastSeen(): string | null {
  try {
    return localStorage.getItem(LAST_SEEN_KEY)
  } catch {
    return null
  }
}

function writeLastSeen(sha: string) {
  try {
    localStorage.setItem(LAST_SEEN_KEY, sha)
  } catch {
    // Private browsing: the popup may show again, nothing worse.
  }
}

/**
 * The changes of this build, once, on the first load after an update. A first
 * visit shows nothing: there is nothing new for someone who just arrived.
 * @returns The changelog to show, and a function to close it.
 */
export function useWhatsNew(): [Changelog | null, () => void] {
  const [changelog, setChangelog] = useState<Changelog | null>(null)

  useEffect(() => {
    const lastSeen = readLastSeen()
    writeLastSeen(__BUILD__.sha)
    if (!lastSeen || lastSeen === __BUILD__.sha) return
    void fetchJson<Changelog>('/changelog.json').then((entry) => {
      if (entry && entry.changes.length > 0) setChangelog(entry)
    })
  }, [])

  return [changelog, () => setChangelog(null)]
}
